/**
 * admin-certilife src/utils/mfa.js(ef8367e) 사본 — 저장 키(mfaTokenReseller·accessTokenReseller·refreshTokenReseller)와
 * 「인증번호가 오지 않을 때」 안내 두 줄(리셀러는 서티라이프 운영팀이 초기화)만 다르다(W6 B1, 설계 3-6).
 *
 * 2차 인증(MFA) 증표·오류 코드·step-up 재시도 규칙 (SPEC-SENDCERT-001 design.md §2·§3).
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/mfa.test.mjs`).
 */
import dayjs from 'dayjs';
import { geoModeTransition } from './geoMode.js';
import * as tokens from './tokenStore.js';

// 증표는 tokenStore 한 곳(일반 탭 localStorage, 지원 탭은 그 탭 sessionStorage). URL 에 싣지 않음. 로그아웃·세션 정리 때 같이 지운다.
export const MFA_TOKEN_KEY = tokens.LEGACY_KEYS.mfa;
export const MFA_HEADER = 'X-Mfa-Token';

export const getMfaToken = () => tokens.getMfaToken();
export const setMfaToken = (token) => {
  if (token) tokens.setMfaToken(token);
};
export const clearMfaToken = () => tokens.clearMfaToken();

/** axios 요청 설정에 증표 헤더를 붙인다(증표가 있을 때만). */
export const withMfaHeader = (config, token = getMfaToken()) => {
  if (token) config.headers[MFA_HEADER] = token;
  return config;
};

export const errorCode = (error) => error?.response?.data?.code;

/**
 * 비밀번호·twoFactorToken·enrollToken 으로 인증하는 로그인 단계 요청.
 * 여기서 나는 401 은 access 토큰 만료가 아니므로 토큰 갱신 후 재전송하면 안 된다
 * (재전송하면 비밀번호 실패 수가 두 번 쌓여 잠금이 앞당겨짐).
 */
export const isLoginRequest = (url = '') =>
  /\/auth\/(login|2fa\/(enroll\/)?(challenge|verify))$/.test(url.split('?')[0]);

const isTwoFactorRequest = (url = '') =>
  url.split('?')[0].includes('/auth/2fa/');

// 수단 이름(design.md §2-3). FALLBACK 은 옛 이름이고 뜻은 "본인 이메일 수단"(D-91)
export const METHOD_LABEL = {
  ALIMTALK_OTP: '휴대폰(카카오 알림톡)',
  FALLBACK_EMAIL_OTP: '이메일',
};

/** 이 앱의 2단계 화면이 처리하는 수단만 남긴다(증표를 주는 수단, D-20). */
export const usableMethods = (methods = []) =>
  methods.filter((m) => METHOD_LABEL[m]);

/**
 * 프로필 `mfa`(design.md §5-3)로 step-up 창의 수단 목록과 보낼 곳(가린 값)을 만든다(§2-3, D-104).
 * 휴대폰이 있으면 ALIMTALK_OTP, 이메일이 있으면 FALLBACK_EMAIL_OTP.
 */
export const factorsOf = (mfa = {}) => {
  const targets = {};
  if (mfa && mfa.phoneMasked) targets.ALIMTALK_OTP = mfa.phoneMasked;
  if (mfa && mfa.fallbackEmailMasked)
    targets.FALLBACK_EMAIL_OTP = mfa.fallbackEmailMasked;
  return { methods: Object.keys(targets), targets };
};

/**
 * 필수 계정(운영자·회사 강제)의 마지막 수단이면 지울 수 없다(서버 TWO_FACTOR_ENFORCED, D-93).
 * 본인 수단만으로 켜진 계정(requiredBy SELF)은 지우면 2FA 가 꺼진다.
 */
export const isLastEnforcedFactor = (mfa = {}) =>
  !!mfa &&
  (mfa.requiredBy === 'OPERATOR' || mfa.requiredBy === 'COMPANY') &&
  factorsOf(mfa).methods.length === 1; // 수단 0개면 지울 것이 없다

// 필수 계정 스위치 잠금 이유(D-132). 화면은 role 이 아니라 서버 requiredBy 만 본다(D-133: intest·stg 운영자는 SELF 일 수 있음)
const REQUIRED_REASON = {
  COMPANY: '회사 정책으로 2차 인증이 필수입니다.',
  OPERATOR: '운영자 계정은 2차 인증이 필수입니다.',
};

/**
 * 「2차 인증 사용」 스위치 상태(A17, D-132). 프로필 `mfa` 로 정한다.
 *  NONE   수단 0개 — 스위치 대신 등록 안내(꺼짐, 필수 계정이어도)
 *  LOCKED 운영자·회사 강제 — 켜짐으로 잠금 + reason (본인이 꺼 둔 값이 있어도 켜짐)
 *  OFF    수단은 있는데 본인이 끔(selfDisabled) — 켤 수 있음
 *  ON     본인 수단으로 켜짐(SELF) — 끌 수 있음
 */
export const twoFactorSwitch = (mfa) => {
  if (!factorsOf(mfa).methods.length) return { kind: 'NONE', on: false };
  const reason = REQUIRED_REASON[mfa.requiredBy];
  if (reason) return { kind: 'LOCKED', on: true, reason };
  return mfa.selfDisabled
    ? { kind: 'OFF', on: false }
    : { kind: 'ON', on: true };
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 수단 등록 대상 값을 서버 형식으로(휴대폰 숫자 `010xxxxxxxx`·이메일), 틀리면 null (A12·A14). */
export const enrollTarget = (method, value = '') => {
  if (method === 'ALIMTALK_OTP') return normalizeMobile(value);
  const email = String(value).trim();
  return method === 'FALLBACK_EMAIL_OTP' && EMAIL_PATTERN.test(email)
    ? email
    : null;
};

/**
 * 세션 중 등록 확인(A15) 본문. SEND step-up 창 안의 등록이면 purpose:'SEND' 를 실어
 * 서버가 증표에 sendAt 을 넣게 한다(D-124) — 등록 뒤 재전송이 발송 인증번호 없이 통과.
 */
export const enrollVerifyBody = (method, code, purpose) =>
  purpose === 'SEND' ? { method, code, purpose } : { method, code };

const STEP_UP_PURPOSE = {
  MFA_REQUIRED: 'LOGIN',
  SEND_AUTH_REQUIRED: 'SEND',
};

export const stepUpPurposeOf = (error) =>
  error?.response?.status === 403
    ? STEP_UP_PURPOSE[errorCode(error)] || null
    : null;

/**
 * MFA_REQUIRED·SEND_AUTH_REQUIRED 를 받으면 step-up 창을 띄우고, 통과하면 원래 요청을 한 번 다시 보낸다(REQ-SEND-007).
 * 사용자가 창을 닫으면 원래 오류에 stepUpCancelled 표시를 붙여 던지고 다시 보내지 않는다
 * (스스로 취소한 것이라 부르는 쪽은 "실패" 알림을 띄우지 않는다, isStepUpCancelled).
 * 같은 목적(LOGIN·SEND)으로는 요청당 한 번만 다시 보낸다 — LOGIN 증표를 받은 뒤 SEND 가 또 필요하면 SEND 창은 한 번 더 뜰 수 있다.
 * 2FA API 자신(`/auth/2fa/*`)의 오류는 창 안에서 보여 주므로 여기서 다루지 않는다.
 * 요청 config 에 skipStepUp 이 있으면(메뉴 배지처럼 사용자가 누르지 않은 조회) 창 없이 그대로 실패시킨다.
 * @returns 처리 대상이 아니면 null, 맞으면 다시 보낸 요청의 Promise
 */
export function retryAfterStepUp(error, { requestStepUp, resend }) {
  const purpose = stepUpPurposeOf(error);
  const config = error?.config;
  if (
    !purpose ||
    !config ||
    config.skipStepUp ||
    isTwoFactorRequest(config.url)
  )
    return null;
  const done = config._stepUpDone || [];
  if (done.includes(purpose)) return null;
  return requestStepUp(purpose).then((passed) => {
    if (!passed) throw markStepUpCancelled(error);
    config._stepUpDone = [...done, purpose];
    return resend(config);
  });
}

/** 본인 확인 창을 사용자가 닫아 실패한 요청인지(실패 알림을 띄우지 않는다, D5) */
export const markStepUpCancelled = (error) => {
  if (error && typeof error === 'object') error.stepUpCancelled = true;
  return error;
};
export const isStepUpCancelled = (error) => !!(error && error.stepUpCancelled);

/**
 * step-up 창 열기 요청을 한 줄로 세운다. 같은 목적이 이미 떠 있으면 그 결과를 같이 기다린다
 * (동시에 실패한 요청 여러 개가 창을 여러 번 띄우지 않게).
 * @param open (purpose) => Promise<boolean> 실제로 창을 띄우는 함수
 */
export function createStepUpQueue(open) {
  let current = null;
  return function requestStepUp(purpose) {
    if (current && current.purpose === purpose) return current.promise;
    const before = current
      ? current.promise.catch(() => false)
      : Promise.resolve();
    const promise = before.then(() => open(purpose));
    const entry = { purpose, promise };
    current = entry;
    const release = () => {
      if (current === entry) current = null;
    };
    promise.then(release, release);
    return promise;
  };
}

/** 서버 ISO 시각을 이 컴퓨터 시간으로 보여 준다. */
export const formatTime = (iso) =>
  iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '-';

// 국외 접속 예외 최대 기간(C4). 서버도 넘으면 400 으로 거절한다
export const GEO_EXEMPTION_MAX_DAYS = 30;
const GEO_EXEMPTION_MAX_MESSAGE = `국외 접속 예외는 최대 ${GEO_EXEMPTION_MAX_DAYS}일까지 걸 수 있습니다.`;

/** 국외 예외 끝 시각 날짜 칸: 오늘 전, 30일 뒤 날짜 다음 날부터는 고를 수 없다. */
export const isGeoUntilDayDisabled = (day, now = dayjs()) =>
  !!day &&
  (day.isBefore(now, 'day') ||
    day.isAfter(now.add(GEO_EXEMPTION_MAX_DAYS, 'day'), 'day'));

/** 국외 예외 끝 시각 검사. 문제가 있으면 안내 문구, 괜찮으면 "". 30일째 날도 지금 시각을 넘으면 거절 */
export const geoUntilError = (until, now = dayjs()) => {
  if (!until || !until.isAfter(now)) return '끝 시각은 지금보다 뒤로 고르세요.';
  if (until.isAfter(now.add(GEO_EXEMPTION_MAX_DAYS, 'day'))) {
    return GEO_EXEMPTION_MAX_MESSAGE;
  }
  return '';
};

export const GEO_BLOCKED_MESSAGE =
  '해외에서는 이용할 수 없습니다. 예외 신청은 서티라이프 운영팀에 해 주세요.';
export const TWO_FACTOR_ENFORCED_MESSAGE =
  '2차 인증이 필수인 계정이라 마지막 수단은 지울 수 없습니다. 다른 수단을 먼저 등록해 주세요.';
// 503 MFA_CHECK_UNAVAILABLE 고정 문구 — crm 운영 필터가 5xx message 를 지우므로 서버 문구를 쓰지 않는다(F-9)
export const MFA_CHECK_UNAVAILABLE_MESSAGE =
  '보안 상태를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.';
const SESSION_NOT_ALLOWED_MESSAGE =
  '이 로그인 방식으로는 2차 인증 수단을 등록·변경할 수 없습니다. 본인 아이디·비밀번호로 로그인해 주세요.';

const serverText = (d, fallback) =>
  typeof d.message === 'string' && d.message ? d.message : fallback;

const MESSAGES = {
  GEO_BLOCKED: (d) =>
    `${GEO_BLOCKED_MESSAGE}${d.ip ? ` (접속 IP: ${d.ip})` : ''}`,
  TWO_FACTOR_NOT_ASSIGNED: () =>
    '2차 인증 수단이 없습니다. 휴대폰 또는 이메일을 먼저 등록해 주세요.',
  TWO_FACTOR_ENFORCED: () => TWO_FACTOR_ENFORCED_MESSAGE,
  // A17 끄기 거절(필수 계정). 서버 문구는 COMPANY 기본값이라 requiredBy 로 고른다
  TWO_FACTOR_REQUIRED_BY_POLICY: (d) =>
    d.requiredBy === 'OPERATOR'
      ? '운영자 계정은 2차 인증이 필수라 끌 수 없습니다.'
      : '회사 정책으로 2차 인증이 필수라 끌 수 없습니다.',
  // 서버 문구 그대로(design.md §2-3), 재시도 없음
  SESSION_NOT_ALLOWED: (d) => serverText(d, SESSION_NOT_ALLOWED_MESSAGE),
  LOGIN_FAILED: (d) =>
    d.remainingAttempts != null
      ? `아이디 또는 비밀번호가 맞지 않습니다. ${d.remainingAttempts}번 더 틀리면 30분 동안 로그인할 수 없습니다.`
      : '아이디 또는 비밀번호가 맞지 않습니다.',
  ACCOUNT_LOCKED: (d) =>
    `비밀번호를 5번 틀려 잠겼습니다.${
      d.lockedUntil ? ` ${formatTime(d.lockedUntil)} 이후 다시 시도하세요.` : ''
    }`,
  OTP_INVALID: (d) =>
    `인증번호가 맞지 않습니다.${
      d.remainingAttempts != null ? ` (남은 횟수 ${d.remainingAttempts}번)` : ''
    }`,
  OTP_EXPIRED: () =>
    '인증번호가 만료됐거나 5번 틀려 더 쓸 수 없습니다. 인증번호를 다시 받아 주세요.',
  // 남은 초는 보내기 버튼에서 줄어들게 보여 주므로 문구에는 멈춘 숫자를 넣지 않는다(D3)
  OTP_RATE_LIMITED: (d) =>
    d.retryAfterSec
      ? '인증번호 요청이 너무 잦습니다. 버튼의 시간이 끝나면 다시 받으세요.'
      : '인증번호 요청이 너무 잦습니다. 잠시 뒤 다시 요청하세요.',
  OTP_SEND_FAILED: (d) =>
    (d.fallbackMethods || []).includes('FALLBACK_EMAIL_OTP')
      ? '인증번호를 보내지 못했습니다. 이메일로 인증번호를 받아 보세요.'
      : '인증번호를 보내지 못했습니다. 잠시 뒤 다시 시도하거나 서티라이프 운영팀에 「2차 인증 초기화」를 요청하세요.',
  MFA_REQUIRED: () => '2차 인증을 마쳐야 할 수 있는 작업입니다.',
  SEND_AUTH_REQUIRED: () => '발송 전 2차 인증을 마쳐야 합니다.',
  MFA_CHECK_UNAVAILABLE: () => MFA_CHECK_UNAVAILABLE_MESSAGE,
  PHONE_DUPLICATED: () => '같은 회사의 다른 계정이 이미 쓰는 번호입니다.',
  EMAIL_DUPLICATED: () => '같은 회사의 다른 계정이 이미 쓰는 이메일입니다.',
  NOT_COMPANY_MANAGER: () => '이 계정에는 이 작업을 할 수 없습니다.',
  ACCOUNT_SCOPE_FORBIDDEN: () => '다른 회사 계정이라 바꿀 수 없습니다.',
};

/** 오류 응답의 code 로 사용자 문구를 만든다. 모르는 코드면 서버 문구, 그것도 없으면 fallback. */
export const errorMessage = (error, fallback) => {
  const data = error?.response?.data || {};
  const build = MESSAGES[data.code];
  if (build) return build(data);
  return serverText(data, fallback);
};

/**
 * 모든 요청의 403 GEO_BLOCKED 면 알림 문구, 아니면 null (AC-GEO-010c).
 * axios 응답 인터셉터 세 곳이 이 문구를 한 번 띄우고 reject 한다(step-up 없음).
 * 2FA API 자신(`/auth/2fa/*`)의 오류는 창 안에 보여 주므로 null.
 */
export const geoBlockedNotice = (error) =>
  error?.response?.status === 403 &&
  errorCode(error) === 'GEO_BLOCKED' &&
  !isTwoFactorRequest(error?.config?.url)
    ? errorMessage(error)
    : null;

/**
 * crm-api·call-api 가 "지금 2FA 가 필요한가"를 inapi 에서 확인하지 못한 503 MFA_CHECK_UNAVAILABLE 이면 고정 문구, 아니면 null.
 * axios 응답 인터셉터 세 곳이 이 문구를 한 번(같은 key) 띄우고 reject 한다 — 재전송·step-up 없음(F-9, design.md §2-2).
 */
export const mfaCheckUnavailableNotice = (error) =>
  error?.response?.status === 503 &&
  errorCode(error) === 'MFA_CHECK_UNAVAILABLE'
    ? MFA_CHECK_UNAVAILABLE_MESSAGE
    : null;

/** 로그인 완료·세션 중 수단 등록·삭제(A13·A15·A16) 응답의 토큰을 저장한다(온 것만). */
export const saveSessionTokens = (data = {}) => {
  if (data.accessToken) tokens.setAccessToken(data.accessToken);
  if (data.refreshToken) tokens.setRefreshToken(data.refreshToken);
  setMfaToken(data.mfaToken);
};

/** access 토큰(JWT) 본문. 서명은 확인하지 않는다(화면 표시용). 읽을 수 없으면 null. */
export const tokenPayload = (token) => {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(part), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
};

/**
 * 목록 행이 로그인한 사람 자신의 계정인지. 토큰의 accountId 로 보고,
 * accountId 가 없는 옛 토큰이면 username 으로 본다.
 * 자기 계정의 상태 조회·예외·초기화를 직접 못 하게 막는 데 쓴다(D-26·D-56·D-57).
 */
export const isSelfAccount = (row, payload) => {
  if (!row || !payload) return false;
  if (payload.accountId) return String(row._id) === String(payload.accountId);
  return !!payload.username && row.username === payload.username;
};

/** 숫자만 남긴 010 휴대폰 번호, 형식이 틀리면 null (design.md §8-1 `010xxxxxxxx`). */
export const normalizeMobile = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  return /^010\d{8}$/.test(digits) ? digits : null;
};

// 로그인 2단계·본인 확인 창 아래 한 줄(D4, 리셀러 앱 문구 — 초기화는 서티라이프 운영팀)
export const NO_CODE_HELP =
  '인증번호가 오지 않나요? 서티라이프 운영팀에 「2차 인증 초기화」를 요청하세요.';

/**
 * 인증번호 칸 값: 숫자만 남기고 6자리까지. 붙여넣은 "123-456"·" 123456" 도 6자리로 들어가야 하므로
 * 칸에 maxlength 를 걸지 않고 여기서 자른다(D2).
 */
export const otpDigits = (value) =>
  String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, 6);

/**
 * 보내기 버튼 글자(D3). 보내기 전·만료 뒤는 「인증번호 받기」, 보낸 뒤는 「다시 받기」.
 * 재전송 대기·429(서버 retryAfterSec) 중이면 남은 초를 붙인다.
 */
export const sendButtonLabel = (sent, cooldown) => {
  const text = sent ? '다시 받기' : '인증번호 받기';
  return cooldown > 0 ? `${text} (${cooldown}초)` : text;
};

// ── 보안 이력 「내용」 칸(D9): detail 원문 대신 문장. 키는 inapi securityLogService.record 호출과 같다 ──
const CHANNEL_LABEL = { ALIMTALK: '휴대폰', EMAIL: '이메일' };
const METHOD_SHORT = { ALIMTALK_OTP: '휴대폰', FALLBACK_EMAIL_OTP: '이메일' };
const PURPOSE_LABEL = {
  LOGIN: '로그인',
  STEP_UP_LOGIN: '작업 중 본인 확인',
  STEP_UP_SEND: '발송 전 본인 확인',
  SETUP: '수단 등록',
};
// ACCOUNT_UPDATE detail.fields(수정 요청 본문 키). 모르는 키는 「기타」
const FIELD_LABEL = {
  name: '이름',
  username: '아이디',
  mobile: '휴대폰 번호',
  email: '이메일',
  password: '비밀번호',
  accountType: '계정 유형',
  duty: '직급',
  deptId: '부서',
  allowedCategories: '메뉴 권한',
};

// 국외 허용 IP 수정 detail 의 바꾼 칸(D-138). 모르는 키는 「기타」
const GEO_ALLOW_FIELD_LABEL = {
  cidr: 'IP·대역',
  scope: '범위',
  companyIds: '회사',
  until: '기간',
  from: '기간',
  indefinite: '기간',
  reason: '사유',
  evidenceRef: '증빙',
};

const GEO_TRIGGER_LABEL = {
  PAGE_OPEN: '화면 열기',
  LOGIN: '로그인 시도',
  REQUEST: '로그인 뒤 요청',
};
const sentence = (...parts) => parts.filter(Boolean).join(' · ');
const words = (...parts) => parts.filter(Boolean).join(' ');
const changed = (label, d) =>
  d.before && d.after
    ? `${label} ${d.before} → ${d.after}로 변경`
    : words(label, d.after || d.before, '등록');
const otpTarget = (d) =>
  sentence(words(CHANNEL_LABEL[d.channel], d.target), PURPOSE_LABEL[d.purpose]);
const verifyText = (d) =>
  sentence(
    PURPOSE_LABEL[d.purpose],
    METHOD_SHORT[d.method] && `${METHOD_SHORT[d.method]}로 인증`
  );
// 기간(D-144): 시작 ~ 끝/무기한. 시작이 없는 옛 이력은 등록·승인 때부터라 「…까지」만
const geoAllowPeriod = (d) => {
  const end = d.indefinite ? '무기한' : d.until && formatTime(d.until);
  if (!d.from) return d.indefinite ? '즉시 ~ 무기한' : end && `${end}까지`;
  return `${formatTime(d.from)} ~ ${end || '-'}`;
};
// 국외 허용 IP 이력(D-138): 대역 · 범위 · 기간 · 사유 · 증빙. 회사는 detail 에 id 만 오므로 수만 보인다
const geoAllowText = (d) =>
  sentence(
    d.cidr,
    d.scope === 'ALL'
      ? '전체 계정'
      : Array.isArray(d.companyIds) && `회사 ${d.companyIds.length}곳`,
    geoAllowPeriod(d),
    d.reason && `사유: ${d.reason}`,
    d.evidenceRef && `증빙: ${d.evidenceRef}`
  );
// 바꾼 칸 키 이름은 inapi 와 맞출 때까지 fields(ACCOUNT_UPDATE 와 같은 이름)·changed 둘 다 읽는다
const geoAllowChanged = (d) => {
  const fields = d.fields || d.changed;
  return Array.isArray(fields) && fields.length
    ? `바꾼 항목: ${[
        ...new Set(fields.map((f) => GEO_ALLOW_FIELD_LABEL[f] || '기타')),
      ].join(', ')}`
    : '';
};

const LOG_DETAIL = {
  TWO_FACTOR_ASSIGN: (d) => changed('휴대폰', d),
  TWO_FACTOR_REMOVE: (d) => words('휴대폰', d.before, '삭제'),
  FALLBACK_EMAIL_SET: (d) =>
    d.removed ? words('이메일', d.before, '삭제') : changed('이메일', d),
  OTP_SENT: otpTarget,
  OTP_SEND_FAIL: otpTarget,
  MFA_SUCCESS: verifyText,
  MFA_FAIL: verifyText,
  SEND_AUTH_SUCCESS: verifyText,
  SEND_AUTH_FAIL: verifyText,
  MFA_EXEMPT_USED: (d) => PURPOSE_LABEL[d.purpose],
  MFA_EXEMPT_ON: (d) =>
    sentence(
      d.hours && `${d.hours}시간`,
      d.until && `${formatTime(d.until)}까지`,
      d.reason && `사유: ${d.reason}`
    ),
  GEO_EXEMPT_ON: (d) =>
    sentence(
      d.until && `${formatTime(d.until)}까지`,
      d.reason && `사유: ${d.reason}`,
      d.evidenceRef && `증빙: ${d.evidenceRef}`
    ),
  // 서버 기록은 막힌 요청(로그인은 requestPage 앱), 화면 보고(via SCREEN_REPORT)는 화면·하던 일·서버까지
  GEO_BLOCKED: (d) =>
    sentence(
      d.page && words('화면', d.app, d.page),
      GEO_TRIGGER_LABEL[d.trigger],
      d.path && words('막힌 요청', d.server, d.method, d.path),
      d.requestPage && `로그인 앱 ${d.requestPage}`
    ),
  GEO_ALLOW_IP_ADDED: geoAllowText,
  GEO_ALLOW_IP_UPDATED: (d) =>
    sentence(d.cidr, geoAllowChanged(d), geoAllowText({ ...d, cidr: '' })),
  GEO_ALLOW_IP_REMOVED: geoAllowText,
  // D-139 고객사 신청 → 운영자 승인·거절, 신청자 취소. 승인 때 운영자가 고친 칸은 수정과 같은 키로 온다
  GEO_ALLOW_IP_REQUESTED: geoAllowText,
  GEO_ALLOW_IP_APPROVED: (d) =>
    sentence(d.cidr, geoAllowChanged(d), geoAllowText({ ...d, cidr: '' })),
  GEO_ALLOW_IP_REJECTED: (d) =>
    sentence(
      d.cidr,
      d.rejectReason && `거절 사유: ${d.rejectReason}`,
      geoAllowText({ ...d, cidr: '' })
    ),
  GEO_ALLOW_IP_CANCELED: geoAllowText,
  // D-142 해외 접속 차단 모드 변경
  GEO_MODE_CHANGED: (d) =>
    sentence(geoModeTransition(d), d.reason && `사유: ${d.reason}`),
  LOCK: (d) => d.lockedUntil && `${formatTime(d.lockedUntil)}까지 로그인 막힘`,
  TWO_FACTOR_RESET: () => '등록된 휴대폰·이메일 삭제',
  // D-132: 본인이 끄고 켜도 수단은 그대로다
  TWO_FACTOR_SELF_DISABLED: () =>
    '로그인 때 인증번호를 묻지 않음 · 등록된 휴대폰·이메일은 그대로',
  TWO_FACTOR_SELF_ENABLED: () => '로그인 때 인증번호를 다시 입력',
  // detail.companyId 는 이 창이 이미 그 회사 계정이라 보일 것이 없다
  POLICY_ON: () => '',
  POLICY_OFF: () => '',
  ACCOUNT_CREATE: (d) => d.username && `아이디 ${d.username}`,
  ACCOUNT_DELETE: (d) => d.username && `아이디 ${d.username}`,
  ACCOUNT_UPDATE: (d) =>
    Array.isArray(d.fields) && d.fields.length
      ? `바꾼 항목: ${[
          ...new Set(d.fields.map((f) => FIELD_LABEL[f] || '기타')),
        ].join(', ')}`
      : '',
  PASSWORD_RESET: (d) => (d.via === 'PHONE_AUTH' ? '본인 휴대폰 인증으로' : ''),
  PERMISSION_CHANGE: (d) =>
    Array.isArray(d.before) && Array.isArray(d.after)
      ? `메뉴 ${d.before.length}개 → ${d.after.length}개`
      : '',
  WHITELIST_LOGIN: () => '사무실 IP에서 공용 비밀번호로 로그인',
  WHITELIST_PW_MODE: (d) =>
    d.pwChecked === 'Y' || d.pwChecked === true
      ? '비번 점검 켬(공용 비밀번호 로그인 막음)'
      : '비번 점검 끔(공용 비밀번호 로그인 허용)',
};

/**
 * 보안 이력 detail 을 읽을 수 있는 문장으로(D9). detail.by(본인·운영자)는 실행자 칸에 쓰므로 뺀다.
 * 모르는 작업·키는 「기타」, 남길 내용이 없으면 "-".
 */
export const securityLogDetail = (action, detail) => {
  if (detail == null) return '-';
  if (typeof detail !== 'object') return '기타';
  const rest = { ...detail };
  delete rest.by;
  const build = LOG_DETAIL[action];
  const text = build ? build(rest) : '';
  if (text) return text;
  return !build && Object.keys(rest).length ? '기타' : '-';
};
